import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GalleryPreview } from "@/components/GalleryPreview";
import { SermonOfTheWeek } from "@/components/SermonOfTheWeek";
import { BrandGraphic } from "@/components/BrandGraphic";
import { ArrowRight, Calendar, Clock, Heart, Users, BookOpen, Video, ExternalLink, Target, Eye, ArrowUpRight, MapPin, Sparkles } from "lucide-react";
import api from "@/lib/api";

const WEEKLY_PROGRAMS = [
  { day: "Tuesday", title: "In the Garden With Jesus", time: "7:00 – 8:00 PM" },
  { day: "Wednesday", title: "Mid-Week Bible Study", time: "8:00 – 9:00 PM" },
  { day: "Friday", title: "Youth Corner", time: "8:00 – 9:00 PM" },
];

// Real church photos — add more URLs here to expand visual variety across the site.
const CHURCH_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_ebenezer-ghana-sda/artifacts/1042bgw3_churc.jpg",
];

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function Home() {
  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/events", { params: { upcoming: true } })
      .then((r) => setEvents(r.data.slice(0, 3)))
      .catch(() => {})
      .finally(() => setEventsLoaded(true));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#003B5C] min-h-screen flex items-center grain-overlay">
        <div className="absolute inset-0">
          {/* Clean navy/gold graphic background */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4A437]/8" />
          <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#D4A437]/5" />
          <div className="absolute inset-0" style={{
            backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 80px)",
          }} />
          <div className="absolute inset-0 bg-gradient-to-br from-[#003B5C]/40 via-transparent to-[#003B5C]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32 lg:py-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4A437]" />
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/85 font-semibold">Richardson, Texas</span>
            </div>
            <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[1.05] text-white">
              Welcome to<br />
              <span className="text-white">Garland Ebenezer Ghanaian</span><br className="hidden sm:block" />
              <span className="text-[#D4A437]"> Seventh-day Adventist Church.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed">
              We are a Ghanaian Seventh-day Adventist community committed to sharing the love of
              Jesus Christ, honoring His Sabbath, and living according to the teachings of the Bible.
              Join us as we grow in faith, serve others, and prepare for His soon return.
            </p>
            <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/visit"
                data-testid="hero-cta-visit"
                className="inline-flex items-center justify-center gap-2 bg-[#C0392B] text-white px-8 py-4 rounded-sm font-semibold hover:bg-[#A93226] transition-all group shadow-lg shadow-[#C0392B]/25"
              >
                Plan Your Visit
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="https://us02web.zoom.us/j/86107004821?pwd=OE9hcjJXRTllSkdPQXlQcGhLU1J2UT09"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-zoom"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 text-white px-8 py-4 rounded-sm font-semibold hover:bg-white/20 transition-all"
              >
                <Video size={18} />
                Join Worship Online
              </a>
            </div>
          </motion.div>
        </div>

        {/* Service times floating card */}
        <div className="absolute bottom-8 right-8 hidden lg:block">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-sm p-6 w-64">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4A437] font-bold mb-5">This Saturday</div>
            <div className="space-y-4 text-white">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/70 mb-1">Sabbath School</div>
                <div className="font-mono text-lg font-semibold text-white">9:30 AM</div>
              </div>
              <div className="border-t border-white/15 pt-4">
                <div className="text-xs uppercase tracking-wider text-white/70 mb-1">Divine Worship</div>
                <div className="font-mono text-lg font-semibold text-white">11:15 AM</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK LINKS — 3 core actions surfaced right after hero */}
      <section data-testid="quick-links-section" className="relative -mt-16 sm:-mt-20 lg:-mt-24 z-10 pb-4">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            <Link
              to="/visit"
              data-testid="quick-link-visit"
              className="group bg-white border border-gray-100 rounded-sm p-7 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-sm bg-[#003B5C] flex items-center justify-center mb-5 group-hover:bg-[#C0392B] transition-colors">
                <MapPin size={20} className="text-white" />
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-2">Step 01</div>
              <h3 className="font-display text-xl font-bold text-[#003B5C] mb-2">Plan Your Visit</h3>
              <p className="text-sm text-[#1E1E1E]/70 leading-relaxed mb-4">
                Directions, parking, what to expect, and everything you need for your first Sabbath with us.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#003B5C] group-hover:gap-3 transition-all">
                Get details <ArrowRight size={14} />
              </span>
            </Link>

            <a
              href="https://us02web.zoom.us/j/86107004821?pwd=OE9hcjJXRTllSkdPQXlQcGhLU1J2UT09"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quick-link-worship-online"
              className="group bg-white border border-gray-100 rounded-sm p-7 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-sm bg-[#003B5C] flex items-center justify-center mb-5 group-hover:bg-[#C0392B] transition-colors">
                <Video size={20} className="text-white" />
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-2">Step 02</div>
              <h3 className="font-display text-xl font-bold text-[#003B5C] mb-2">Join Worship Online</h3>
              <p className="text-sm text-[#1E1E1E]/70 leading-relaxed mb-4">
                Can&apos;t make it in person? Worship with us live every Sabbath through our Zoom livestream.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#003B5C] group-hover:gap-3 transition-all">
                Open Zoom link <ExternalLink size={14} />
              </span>
            </a>

            <Link
              to="/explore"
              data-testid="quick-link-bible-explorer"
              className="group bg-white border border-gray-100 rounded-sm p-7 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-sm bg-[#003B5C] flex items-center justify-center mb-5 group-hover:bg-[#C0392B] transition-colors">
                <Sparkles size={20} className="text-white" />
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-2">Step 03</div>
              <h3 className="font-display text-xl font-bold text-[#003B5C] mb-2">Bible Explorer</h3>
              <p className="text-sm text-[#1E1E1E]/70 leading-relaxed mb-4">
                Ask questions and study Scripture with guidance rooted in the Seventh-day Adventist beliefs.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#003B5C] group-hover:gap-3 transition-all">
                Start exploring <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section data-testid="mission-section" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">
                <Target size={14} /> Our Mission
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#003B5C]">
                Our Mission.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-6">
              <p className="text-lg sm:text-xl leading-relaxed text-[#1E1E1E]/80">
                Our mission is to love God, love others, and exalt Jesus Christ by reaching all
                people, including the Ghanaian community, through Bible-based teaching and
                <span className="text-[#003B5C] font-semibold"> Sabbath-centered worship.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section data-testid="vision-section" className="py-24 lg:py-32 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">
                <Eye size={14} /> Our Vision
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#003B5C]">
                Our Vision.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-6">
              <p className="text-lg sm:text-xl leading-relaxed text-[#1E1E1E]/80">
                Our vision is to glorify God and Jesus Christ by making true disciples,
                advancing the gospel through mission, strengthening believers, and
                <span className="text-[#003B5C] font-semibold"> honoring Christ in all we do.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT ADVENTISTS — preview with external CTA */}
      <section data-testid="about-adventists-preview" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">About Seventh-day Adventists</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight mb-6">
                A worldwide family of faith.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-[#1E1E1E]/75 mb-5">
                The Seventh-day Adventist Church is a mainstream Protestant church with millions
                of members worldwide. We seek to enhance the quality of life for people everywhere
                and to share the hope that Jesus is coming again soon.
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-[#1E1E1E]/75 mb-8">
                Discover the beliefs, heritage, and global mission that shape who we are.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://adventist.org/en/beliefs"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-about-adventists-cta"
                  className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-7 py-3.5 rounded-sm font-medium hover:bg-[#002A42] transition-all group"
                >
                  FIND OUT MORE
                  <ExternalLink size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <Link
                  to="/about"
                  data-testid="home-about-page-cta"
                  className="inline-flex items-center gap-2 border-2 border-[#003B5C] text-[#003B5C] px-7 py-3.5 rounded-sm font-medium hover:bg-[#003B5C] hover:text-white transition-all"
                >
                  Our Story
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-sm bg-[#003B5C]">
                <img
                  src={CHURCH_PHOTOS[0]}
                  alt="Garland Ebenezer Ghanaian Seventh-day Adventist Church congregation"
                  data-testid="about-adventists-church-photo"
                  className="w-full h-[420px] sm:h-[480px] lg:h-[560px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#003B5C]/40 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-sm px-4 py-3">
                  <div className="w-9 h-9 rounded-sm bg-[#003B5C] flex items-center justify-center flex-shrink-0">
                    <img src="/logo.png" alt="" className="w-6 h-6 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-xs font-bold text-[#003B5C]">Our Church Family</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A437] font-semibold">Garland, Texas</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PASTOR PREVIEW — small welcome card; full message lives on /about */}
      <section data-testid="pastor-preview-section" className="py-24 lg:py-32 bg-[#F7F8FA]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm grid grid-cols-1 md:grid-cols-12 gap-0">
            <div className="md:col-span-5 lg:col-span-4 bg-[#003B5C] relative">
              <div className="aspect-[4/5] md:aspect-auto md:h-full overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_ebenezer-ghana-sda/artifacts/fkebmt9v_1779417898380.png"
                  alt="Pastor Abraham Binzuwah Jr."
                  data-testid="pastor-preview-photo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="md:col-span-7 lg:col-span-8 p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">
                Welcome From the Pastor
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#003B5C] leading-tight mb-5">
                Welcome From the Pastor.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-[#1E1E1E]/75 mb-7">
                Welcome to Garland Ebenezer Ghanaian Seventh-day Adventist Church. We are blessed that you are here.
                Our prayer is that you experience the love of Christ, the truth of God's Word, and
                the warmth of a caring church family as we worship and grow together in faith.
              </p>
              <div className="mb-7">
                <p className="font-display text-base font-bold text-[#003B5C]">Pastor Abraham Binzuwah Jr.</p>
                <p className="text-sm text-[#1E1E1E]/60">Garland Ebenezer Ghanaian Seventh-day Adventist Church</p>
              </div>
              <Link
                to="/about"
                data-testid="pastor-preview-cta"
                className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-7 py-3.5 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all self-start group"
              >
                Read Full Message
                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW — Life at Garland Ebenezer */}
      <GalleryPreview />

      {/* SERVICE TIMES - Bento */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-16 max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">Service Times</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight">
              Join us this Sabbath.
            </h2>
          </div>          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 bg-[#003B5C] text-white p-10 lg:p-14 rounded-sm">
              <div className="text-[#D4A437] font-bold uppercase tracking-[0.2em] text-xs mb-5">Saturday</div>
              <div className="space-y-6">
                <div className="border-b border-white/20 pb-6">
                  <div className="text-2xl sm:text-3xl font-display font-bold mb-1">Sabbath School</div>
                  <div className="text-3xl sm:text-4xl font-display font-black text-[#D4A437]">9:30 AM</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-display font-bold mb-1">Divine Worship</div>
                  <div className="text-3xl sm:text-4xl font-display font-black text-[#D4A437]">11:15 AM</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
              {WEEKLY_PROGRAMS.map((p) => (
                <div key={p.day} data-testid={`weekly-${p.day.toLowerCase()}`} className="bg-[#F7F8FA] border border-gray-100 p-6 rounded-sm hover:border-[#D4A437] transition-colors">
                  <div className="text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                    <Clock size={12} /> {p.day}
                  </div>
                  <div className="font-display text-lg font-bold text-[#003B5C]">{p.title}</div>
                  <div className="text-sm text-[#1E1E1E]/60 mt-1">{p.time}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED EVENTS */}
      <section data-testid="upcoming-events-section" className="py-24 lg:py-32 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">What's Happening</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C]">Upcoming Events</h2>
            </div>
            <Link to="/events" data-testid="home-events-cta" className="inline-flex items-center gap-2 text-[#003B5C] font-semibold border-b-2 border-[#D4A437] pb-1 hover:gap-3 transition-all">
              All events <ArrowRight size={16} />
            </Link>
          </div>
          {events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.map((ev) => (
                <Link to="/events" key={ev.id} data-testid={`home-event-card-${ev.id}`} className="block bg-white border border-gray-100 rounded-sm p-8 hover:shadow-lg hover:-translate-y-1 transition-all">
                  <div className="flex items-center gap-2 text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-4">
                    <Calendar size={12} />{" "}
                    {ev.event_type === "weekly"
                      ? `Every ${ev.recurrence_day}${ev.start_time ? ` · ${ev.start_time}` : ""}`
                      : ev.event_type === "date_range" && ev.end_date
                        ? `${formatDate(ev.date)} – ${formatDate(ev.end_date)}`
                        : formatDate(ev.date)}
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#003B5C] leading-tight">{ev.title}</h3>
                  {ev.speaker && <div className="text-sm text-[#1E1E1E]/60 mt-3">with {ev.speaker}</div>}
                </Link>
              ))}
            </div>
          ) : (
            <div
              data-testid="upcoming-events-empty"
              className="bg-white border border-dashed border-gray-200 rounded-sm p-10 sm:p-14 text-center"
            >
              <Calendar size={28} className="text-[#D4A437] mx-auto mb-4" />
              <p className="font-display text-lg sm:text-xl font-semibold text-[#003B5C] mb-2">
                {eventsLoaded ? "No upcoming events — check back soon." : "Loading events…"}
              </p>
              {eventsLoaded && (
                <p className="text-sm text-[#1E1E1E]/60 max-w-md mx-auto">
                  Join us every Sabbath for worship, and follow our updates for special programs and gatherings.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* SERMON OF THE WEEK — auto-pulls latest from YouTube */}
      <SermonOfTheWeek />

      {/* CTA FOOTER STRIP */}
      <section className="bg-[#003B5C] py-20 lg:py-28 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Heart size={36} className="text-[#D4A437] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-white">
            We can't wait to meet you.
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
            Come as you are. Bring your questions, your hopes, your family. There's a seat saved for you.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/visit" data-testid="cta-strip-visit" className="inline-flex items-center justify-center gap-2 bg-[#D4A437] text-white px-8 py-4 rounded-sm font-medium hover:bg-[#B88C2A] transition-all">
              Plan Your Visit <ArrowRight size={18} />
            </Link>
            <Link to="/contact" data-testid="cta-strip-contact" className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 rounded-sm font-medium hover:bg-white/10 transition-all">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
